import { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Activity, Eye, EyeOff, LogIn } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  if (user) {
    const next = loc.state?.from || (user.must_change_password ? "/change-password" : "/");
    return <Navigate to={next} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password, remember);
      toast.success(`Welcome back, ${u.name || u.email}`);
      // Navigate happens via the user state change in the next render.
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Sign-in failed", { description: detail });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6" data-testid="login-page">
      <Card className="w-full max-w-md p-8 border border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 grid place-items-center bg-zinc-950 text-white rounded-md">
            <Activity className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div className="font-display text-xl font-bold tracking-tight text-zinc-950">
            Living Energy <span className="text-zinc-400">·</span> Hub
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-950">Sign in</h1>
        <p className="mt-1 text-sm text-zinc-600">
          Use your account credentials to access the dashboard.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 bg-white"
            />
          </div>
          <div>
            <Label htmlFor="login-password">Password</Label>
            <div className="mt-1.5 relative">
              <Input
                id="login-password"
                data-testid="login-password-input"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                data-testid="toggle-password-visibility"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                aria-label="Toggle password visibility"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer select-none">
            <Checkbox
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
              data-testid="login-remember-me"
            />
            Remember me on this device
          </label>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <LogIn className="h-4 w-4 mr-1.5" />
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-sm text-zinc-600 hover:text-zinc-950 underline-offset-2 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
